import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';
import { fetchPrototype, parsePrototypeFilename } from '../lib/prototypeFetch';
import AnnotationList from './AnnotationList';
import type { Annotation } from './AnnotationOverlay';
import { GUIDED_REVIEW_QUESTIONS, buildGuidedAnnotationComment, parseGuidedAnnotation, type GuidedQuestion } from '../lib/guidedReviewQuestions';

// DEF-9 (AC6.4 client-portal access) — "Open prototype" / "Download" controls
// + per-screen annotation for the `prototype` artefact TYPE (the Prototype
// Agent's generated HTML), on the artefact detail page. Before this, there
// was NO frontend caller of the existing, correctly-versioned
// GET .../versions/:versionId/prototype route or its sibling annotation
// routes — a "prototype" artefact fell through to ArtefactDetailContent's
// generic JSON-envelope fallback (the raw `{ html, fidelity, screenIds }`
// blob), unreadable and un-openable.
//
// VIEWING MECHANISM — R2 (codex) CRITICAL finding, addressed here:
// the first version of this component fetched the HTML (Bearer-authenticated
// — this app's normal session has NO auth cookie, only a localStorage JWT, so
// a plain <a href> new-tab navigation to the API origin can't authenticate)
// and opened it via `window.open(URL.createObjectURL(blob), '_blank')`. That
// is NOT safe: a `blob:` URL created by this page inherits THIS PAGE'S OWN
// origin for scripting purposes (unlike a real cross-origin navigation) — any
// inline <script> in the prototype HTML (a real, shipped MEDIUM/HIGH-fidelity
// feature, not incidental) would run with access to this app's own
// `localStorage` (including the `blueprint_token` Bearer JWT) and could call
// this app's own API as the signed-in user. `noopener,noreferrer` only cuts
// `window.opener`/referrer — it does NOT change the blob document's origin or
// script privileges, so it does not mitigate this.
//
// FIX: "Open prototype" now renders the fetched HTML INLINE, in a SANDBOXED
// iframe — reusing this codebase's own proven security contract from
// ScreenPreviewCard (its "H1" finding): `sandbox=""`, EMPTY, never
// `allow-scripts` and never `allow-same-origin`. A sandboxed iframe with no
// `allow-same-origin` gets an opaque, unique origin regardless of what's
// loaded into it — inline scripts (if the renderer even executes them, which
// empty sandbox prevents outright) could not reach this app's `localStorage`,
// cookies, or same-origin fetch credentials even in principle. The tradeoff:
// MEDIUM/HIGH fidelity's inline-script interactivity does not run in this
// inline preview (mirrors ScreenPreviewCard's own accepted tradeoff for
// screen_spec previews). Full interactivity is still available by
// Download-ing the file and opening it locally — a `file://` context has no
// origin relationship to this app at all, so there is no token/session to
// steal there either. This is a deliberate pivot away from this story's
// original "open via the server route in a new tab" preference: a plain new-
// tab navigation can't authenticate (Bearer-token-only sessions, no cookie),
// and opening a blob URL directly via `window.open` is the exact
// vulnerability above (a NEW, unsandboxed top-level context inherits the
// blob's creator-origin for scripting) — sandboxed inline iframe is the safe
// option actually available here.
//
// DEF-15 — the iframe now loads via a Blob URL (`src`) instead of `srcDoc`,
// so the frame's own navigation (see below, near the guided-questions
// screen picker) can carry a `#screenId` fragment for native anchor-scroll —
// `srcDoc` has no URL to hang a fragment off. This is NOT the vulnerable
// pattern above: the blob URL here is loaded INTO this same `sandbox=""`
// iframe (still no `allow-same-origin`, still an opaque origin regardless of
// the blob's nominal creator-origin), never handed to `window.open` or any
// other unsandboxed context.
//
// Download stays fetch+blob+`<a download>`: a download never EXECUTES the
// HTML in any browsing context (the browser just saves bytes), so it carries
// none of the viewing risk above.
//
// ANNOTATION UI — deliberately NOT a live click-to-pin overlay (the
// ScreenPreviewCard/screen_spec precedent): that pattern needs a per-SCREEN
// rendered surface to click on (screen_spec has a dedicated
// GET .../screens/:screenId/render endpoint for exactly that). The
// `prototype` artefact type has no equivalent — its one stored HTML document
// is the whole multi-screen prototype, not addressable per screen. Rather
// than build new per-screen rendering to support a pin overlay (a much larger
// change), this ships a simpler but fully wired comment composer: pick a
// screen (from the version's own stored screenIds), read/write comments via
// the EXACT SAME versioned annotation routes, pinned at a fixed center point
// (xPct/yPct = 0.5). AnnotationList (shared with ScreenPreviewCard) renders
// the resulting thread.

export interface PrototypeVersionInput {
  id: string;
  version: string;
  createdAt: string;
  notes: string | null;
}

interface Props {
  artefactId: string;
  versions: PrototypeVersionInput[];
}

function parseScreenIds(notes: string | null): string[] {
  if (!notes) return [];
  try {
    const parsed = JSON.parse(notes) as { screenIds?: unknown };
    return Array.isArray(parsed.screenIds) ? parsed.screenIds.filter((s): s is string => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

interface PrototypeRole {
  idx: number;
  label: string;
}

// PIECE-0 (security recon: Sprint-Artefacts/prototype-interactive-viewer-security-recon.md,
// [[project_prototype_viewer_security_recon]]) — the role list is read
// directly off the ALREADY-FETCHED prototype HTML (the stored `notes` JSON
// does not persist roleViews separately from the rendered document, so
// that data-derivation path does not exist — see this component's PR
// description for that finding). DOMParser.parseFromString builds a
// DETACHED document: it does not execute <script> tags and is never
// inserted into the live DOM, so reading text/attributes off it carries no
// XSS risk even though `html` is untrusted renderer output. Every role
// button (both the gate's and the switcher's) carries the SAME
// data-role-idx index space (highFidelity.ts's Q4-locked mechanism), so
// reading either set is equivalent — .switcher__btn is used here since the
// switcher (unlike the gate) is always present whenever roleViews.showSwitcher
// is true. No buttons found => a 0/1-persona (non-switcher) prototype =>
// an empty role list => the chrome renders absent, not broken.
// Exported for direct unit coverage (R1 review nice-to-have) — otherwise
// only exercised indirectly through the component's own render tests.
export function parsePrototypeRoles(html: string): PrototypeRole[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.querySelectorAll('.switcher__btn[data-role-idx]'))
    .map((btn) => ({ idx: Number(btn.getAttribute('data-role-idx')), label: btn.textContent?.trim() ?? '' }))
    .filter((r) => Number.isInteger(r.idx))
    .sort((a, b) => a.idx - b.idx);
}

export interface ScreenNavEntry {
  id: string;
  name: string;
}

// DEF-15 REWORK (issue #2329) — root cause was never the Blob-URL/fragment
// mechanism (confirmed sound in isolation): highFidelity.ts CSS-gates screen
// visibility per active role whenever the prototype has a switcher
// (`.screen{display:none}` base, revealed only via
// `body[data-active-role-idx="N"] .screen[data-visible-for-idx~="N"]`), but
// the "Annotate screen" dropdown used to list EVERY persona's screenIds
// (from the version's pinned `notes.screenIds`) with no regard to which role
// is currently active. Selecting a screen belonging to a non-active persona
// pointed the iframe's #fragment at a `display:none` section — no browser
// can scroll to an element that isn't being rendered, regardless of how
// correctly the fragment/id match.
//
// Fix: read the id+name pairs straight off the nav block(s) highFidelity.ts
// already renders per role (`navBlocks`/`renderNav` — the SAME markup the
// user sees in the prototype's own sidebar), scoped to the currently active
// role via `.nav-wrap[data-for-role-idx="N"]`. This can never drift from
// "what's actually display:block for the active role" because it IS that
// role's own rendered nav, not a second, independently-computed list. A
// non-switcher (flat) prototype renders exactly one un-wrapped `.nav` — the
// only nav in the document already lists every screen (no role gating
// exists in that mode), so `roleIdx: null` reads it directly.
//
// PIECE-0's `restampPrototypeRoleState` (server) only rewrites `<body>`'s
// two role-state attributes — every role's nav-wrap is present in EVERY
// fetched HTML variant regardless of which `?roleIdx` was requested, so this
// parse is correct against the html already held in state, no extra fetch.
//
// Names: the nav's own link text is the SAME `cardHeading` string
// highFidelity.ts uses for the on-screen `<h2>` (DEF-11), so the dropdown
// label always matches what the screen itself is titled — more useful than
// the positional id ("screen-7"). The PRD-authored `name` field is
// schema-enforced non-empty (`prdJsonSchema.ts`), but names are NOT enforced
// unique across screens — two screens can legitimately share a rendered
// name (e.g. two personas' own "Dashboard"), so any name repeated within
// the same scoped list gets its id appended to stay distinguishable; an
// empty/unparseable name (defensive — not reachable from today's schema)
// falls back to the id outright.
//
// R1 review finding — roleScreenVisibility.ts explicitly supports an
// EXPLICIT EMPTY rolePermissions entry for a persona ("this role sees
// nothing", a real PRD-authored state, not an error) — renderNav still
// renders that role's `<nav>` wrapper, just with zero `.nav__item`
// children. That case must NOT be confused with "no nav element exists at
// all" (not-yet-opened, or a malformed/legacy document) — the caller's
// fallback to the flat, unscoped screenIds is only correct for the latter;
// falling back for the former would reintroduce exactly the DEF-15 bug for
// that one role. Returning `null` (nav missing) vs `[]` (nav present, empty)
// lets the caller tell them apart.
export function parseScreenNavEntries(html: string, roleIdx: number | null): ScreenNavEntry[] | null {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const scopedNav = roleIdx !== null ? doc.querySelector(`.nav-wrap[data-for-role-idx="${roleIdx}"] .nav`) : null;
  const nav = scopedNav ?? doc.querySelector('.nav');
  if (!nav) return null;
  const raw = Array.from(nav.querySelectorAll('a.nav__item[href^="#"]'))
    .map((a) => {
      const id = (a.getAttribute('href') ?? '').slice(1);
      const name = a.textContent?.trim() || id;
      return { id, name };
    })
    .filter((entry) => entry.id);
  const nameCounts = new Map<string, number>();
  raw.forEach((entry) => nameCounts.set(entry.name, (nameCounts.get(entry.name) ?? 0) + 1));
  return raw.map((entry) => (nameCounts.get(entry.name)! > 1 ? { ...entry, name: `${entry.name} · ${entry.id}` } : entry));
}

export default function PrototypeViewer({ artefactId, versions }: Props) {
  // versions arrives newest-first (the generic /versions list route orders
  // by createdAt DESC) — the picker still needs to offer every retained
  // version (AC4/L5), so no filtering here, just a stable ordinal label.
  const options = useMemo(
    () => versions.map((v, idx) => ({ ...v, n: versions.length - idx })),
    [versions],
  );

  const [selectedVersionId, setSelectedVersionId] = useState(options[0]?.id ?? '');
  const effectiveVersionId = options.some((o) => o.id === selectedVersionId) ? selectedVersionId : (options[0]?.id ?? '');
  const selectedVersion = options.find((o) => o.id === effectiveVersionId) ?? null;

  const [openError, setOpenError] = useState('');
  const [opening, setOpening] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  // PIECE-0 — null means "default state" (role 0 / not-yet-selected); the
  // chrome renders role 0 as active in that case too, matching the
  // renderer's own default (data-active-role-idx="0" pre-stamped by
  // highFidelity.ts whenever showSwitcher is true).
  const [activeRoleIdx, setActiveRoleIdx] = useState<number | null>(null);
  const roles = useMemo(() => (previewHtml !== null ? parsePrototypeRoles(previewHtml) : []), [previewHtml]);

  const screenIds = useMemo(() => parseScreenIds(selectedVersion?.notes ?? null), [selectedVersion]);
  const [selectedScreenId, setSelectedScreenId] = useState('');

  // DEF-15 REWORK — the dropdown must offer only screens reachable (visible)
  // for the CURRENTLY ACTIVE role, or selecting one points the iframe's
  // #fragment at a display:none section (see parseScreenNavEntries above).
  // `activeRoleIdx === null` while `roles.length > 0` means "not yet
  // switched" — the renderer's own default is role 0 (highFidelity.ts stamps
  // `data-active-role-idx="0"`), matching this file's existing convention
  // for `roles.map`'s `isActive` check above.
  const activeNavRoleIdx = roles.length > 0 ? (activeRoleIdx ?? 0) : null;
  const screenNavEntries = useMemo(
    () => (previewHtml !== null ? parseScreenNavEntries(previewHtml, activeNavRoleIdx) : null),
    [previewHtml, activeNavRoleIdx],
  );
  // Fall back to the flat, unscoped notes-derived screenIds (id-as-label)
  // ONLY when there's genuinely no nav to scope by — before the preview is
  // ever opened, or (defensively) a malformed/legacy document with no `.nav`
  // element at all. A role whose OWN nav exists but is legitimately empty
  // (an explicit `rolePermissions: []` entry — "this role sees nothing",
  // supported by roleScreenVisibility.ts) must NOT fall back to the global
  // union — `screenNavEntries` is `[]`, not `null`, in that case, and `[]`
  // IS the correct, final answer: an empty dropdown, not a re-opened DEF-15.
  const screenOptions: ScreenNavEntry[] =
    screenNavEntries !== null ? screenNavEntries : screenIds.map((id) => ({ id, name: id }));
  const optionIds = useMemo(() => screenOptions.map((o) => o.id), [screenOptions]);
  const effectiveScreenId = optionIds.includes(selectedScreenId) ? selectedScreenId : (optionIds[0] ?? '');

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [annotationsError, setAnnotationsError] = useState('');
  const [draftComment, setDraftComment] = useState('');
  const [saving, setSaving] = useState(false);

  // Piece 3 — guided review questions. Draft answers keyed by question id
  // (not by screen — mirrors draftComment's own existing behavior of not
  // resetting on screen switch, for consistency, not because it's ideal).
  // "Already answered for this screen" is derived, not stored: scan this
  // screen's own already-fetched `annotations` for a comment carrying this
  // question's label (parseGuidedAnnotation) — no new API, no new state to
  // keep in sync when the screen changes.
  const [guidedDrafts, setGuidedDrafts] = useState<Record<string, string>>({});
  const [guidedSavingId, setGuidedSavingId] = useState<string | null>(null);
  const answeredGuidedLabels = useMemo(
    () => new Set(annotations.map((a) => parseGuidedAnnotation(a.comment)?.label).filter((l): l is string => !!l)),
    [annotations],
  );

  useEffect(() => {
    // Switching version invalidates any open preview — it was fetched for
    // the PREVIOUS versionId. DEF-15 — this used to also fire on SCREEN
    // change (a stale, over-broad dependency), which closed the open
    // preview iframe entirely every time the "Annotate screen" dropdown
    // changed. Screen selection doesn't invalidate the fetched HTML (same
    // version, same document) — it's a within-document navigation concern,
    // handled below by previewSrc's hash instead.
    setPreviewHtml(null);
    setActiveRoleIdx(null);
  }, [artefactId, effectiveVersionId]);

  useEffect(() => {
    if (!effectiveVersionId || !effectiveScreenId) { setAnnotations([]); return; }
    let cancelled = false;
    apiFetch<{ annotations: Annotation[] }>(
      `/artefacts/${artefactId}/versions/${effectiveVersionId}/screens/${encodeURIComponent(effectiveScreenId)}/annotations`,
    )
      .then((res) => { if (!cancelled) { setAnnotations(Array.isArray(res.annotations) ? res.annotations : []); setAnnotationsError(''); } })
      .catch((err) => { if (!cancelled) { setAnnotations([]); setAnnotationsError(err instanceof Error ? err.message : 'Failed to load annotations'); } });
    return () => { cancelled = true; };
  }, [artefactId, effectiveVersionId, effectiveScreenId]);

  // DEF-15 — the prototype FRAME is a single long scroll of stacked
  // <section id="${screenId}"> screens (highFidelity.ts renderScreenCard),
  // rendered inside a sandbox="" iframe with no allow-same-origin: the
  // resulting document has an OPAQUE origin distinct from this page even
  // though it's srcdoc-derived (sandboxed-origin flag), so contentDocument/
  // contentWindow access from here throws cross-origin — no scrollIntoView,
  // no postMessage (nothing in the iframe can run script to listen for one
  // anyway). The only thing the PARENT can always do to ANY iframe it owns,
  // regardless of the child's origin or sandboxing, is repoint its OWN `src`
  // attribute — a same-document URL that differs only by #fragment is a
  // native browser anchor-scroll (no reload, no script involved on either
  // side). `srcDoc` has no URL to carry a fragment on, so the frame now
  // navigates via a Blob URL (same safe-download mechanism this file
  // already uses below) with the selected screen's id appended as #hash —
  // this is the "scroll driven from the app side" the recon called for.
  const previewObjectUrl = useMemo(() => {
    if (previewHtml === null) return null;
    return URL.createObjectURL(new Blob([previewHtml], { type: 'text/html' }));
  }, [previewHtml]);

  useEffect(() => {
    return () => { if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl); };
  }, [previewObjectUrl]);

  const previewSrc = previewObjectUrl === null
    ? undefined
    : effectiveScreenId
      ? `${previewObjectUrl}#${encodeURIComponent(effectiveScreenId)}`
      : previewObjectUrl;

  async function handleOpen() {
    if (!effectiveVersionId) return;
    setOpening(true);
    setOpenError('');
    try {
      // No roleIdx — byte-for-byte the same request as before PIECE-0.
      const res = await fetchPrototype(artefactId, effectiveVersionId, {});
      const html = await res.text();
      setPreviewHtml(html);
      setActiveRoleIdx(null);
      // DEF-15 — a prior preview session may have left the "Annotate
      // screen" dropdown pointing at a far screen (e.g. screen-19). Opening
      // always starts the frame at the top of the document, so reset the
      // dropdown to match — a coherent, known starting state (both at
      // screen-1) rather than a frame/dropdown desync.
      setSelectedScreenId(screenIds[0] ?? '');
    } catch (err) {
      setOpenError(err instanceof Error ? err.message : 'Failed to open prototype');
    } finally {
      setOpening(false);
    }
  }

  // PIECE-0 — app-native role switch: re-requests the SAME version with
  // ?roleIdx=idx and swaps srcDoc via this new HTML. Executes ZERO
  // prototype JS — the server does the <body> attribute re-stamp
  // (restampPrototypeRoleState) and the browser just renders new markup,
  // the exact ScreenPreviewCard Prev/Next pattern (#1419) applied to roles.
  async function handleSelectRole(idx: number) {
    if (!effectiveVersionId) return;
    setOpening(true);
    setOpenError('');
    try {
      const res = await fetchPrototype(artefactId, effectiveVersionId, { roleIdx: idx });
      const html = await res.text();
      setPreviewHtml(html);
      setActiveRoleIdx(idx);
    } catch (err) {
      setOpenError(err instanceof Error ? err.message : 'Failed to switch role');
    } finally {
      setOpening(false);
    }
  }

  async function handleDownload() {
    if (!effectiveVersionId) return;
    setDownloading(true);
    setOpenError('');
    try {
      const res = await fetchPrototype(artefactId, effectiveVersionId, { download: true });
      const filename = parsePrototypeFilename(res.headers.get('content-disposition'), `prototype_v${selectedVersion?.n ?? 1}.html`);
      const blob = await res.blob();
      // Safe: a download never executes the HTML in any browsing context —
      // the browser only saves bytes. No sandboxing concern here (contrast
      // handleOpen above).
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setOpenError(err instanceof Error ? err.message : 'Failed to download prototype');
    } finally {
      setDownloading(false);
    }
  }

  function submitAnnotation() {
    const comment = draftComment.trim();
    if (!comment || !effectiveVersionId || !effectiveScreenId) return;
    setSaving(true);
    apiFetch<Annotation>(
      `/artefacts/${artefactId}/versions/${effectiveVersionId}/screens/${encodeURIComponent(effectiveScreenId)}/annotations`,
      { method: 'POST', body: JSON.stringify({ xPct: 0.5, yPct: 0.5, comment }) },
    )
      .then((created) => { setAnnotations((prev) => [...prev, created]); setDraftComment(''); setAnnotationsError(''); })
      .catch((err) => setAnnotationsError(err instanceof Error ? err.message : 'Failed to save annotation'))
      .finally(() => setSaving(false));
  }

  // Piece 3 — answering a guided question creates an annotation on the
  // CURRENT screen through the EXACT SAME create path as a free comment
  // (same route, same center-pin sentinel xPct/yPct=0.5) — the only
  // difference is the stored comment carries the question's label as a
  // prefix (buildGuidedAnnotationComment), so no schema change and no new
  // access gate: this route already grants CLIENT_SPONSOR parity (DEF-9).
  function submitGuidedAnswer(question: GuidedQuestion) {
    const answer = (guidedDrafts[question.id] ?? '').trim();
    if (!answer || !effectiveVersionId || !effectiveScreenId) return;
    setGuidedSavingId(question.id);
    apiFetch<Annotation>(
      `/artefacts/${artefactId}/versions/${effectiveVersionId}/screens/${encodeURIComponent(effectiveScreenId)}/annotations`,
      { method: 'POST', body: JSON.stringify({ xPct: 0.5, yPct: 0.5, comment: buildGuidedAnnotationComment(question, answer) }) },
    )
      .then((created) => {
        setAnnotations((prev) => [...prev, created]);
        setGuidedDrafts((prev) => ({ ...prev, [question.id]: '' }));
        setAnnotationsError('');
      })
      .catch((err) => setAnnotationsError(err instanceof Error ? err.message : 'Failed to save answer'))
      .finally(() => setGuidedSavingId(null));
  }

  if (options.length === 0) {
    return (
      <div style={{ padding: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
        No prototype has been generated yet.
      </div>
    );
  }

  return (
    <div className="prototype-viewer" data-testid="prototype-viewer">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <label htmlFor="prototype-version-select" style={{ fontSize: 13, fontWeight: 600 }}>Version</label>
        <select
          id="prototype-version-select"
          data-testid="prototype-version-select"
          value={effectiveVersionId}
          onChange={(e) => setSelectedVersionId(e.target.value)}
        >
          {options.map((o) => (
            <option key={o.id} value={o.id}>v{o.n} ({o.version}) — {new Date(o.createdAt).toLocaleDateString()}</option>
          ))}
        </select>

        <button
          type="button"
          data-testid="prototype-open"
          disabled={opening || !effectiveVersionId}
          onClick={handleOpen}
          style={{
            fontSize: 13, fontWeight: 600, padding: '6px 14px', borderRadius: 6,
            border: 'none', background: 'var(--primary, #7c3aed)', color: '#fff',
            cursor: opening ? 'default' : 'pointer',
          }}
        >
          {opening ? 'Opening…' : 'Open prototype'}
        </button>

        <button
          type="button"
          data-testid="prototype-download"
          disabled={downloading || !effectiveVersionId}
          onClick={handleDownload}
          style={{
            fontSize: 13, fontWeight: 600, padding: '6px 14px', borderRadius: 6,
            border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)',
            cursor: downloading ? 'default' : 'pointer',
          }}
        >
          {downloading ? 'Downloading…' : 'Download'}
        </button>

        {previewHtml !== null && (
          <button
            type="button"
            data-testid="prototype-close-preview"
            onClick={() => { setPreviewHtml(null); setActiveRoleIdx(null); }}
            style={{
              fontSize: 13, fontWeight: 600, padding: '6px 14px', borderRadius: 6,
              border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', cursor: 'pointer',
            }}
          >
            Close preview
          </button>
        )}
      </div>

      {openError && (
        <p role="alert" style={{ color: 'var(--error, #dc2626)', fontSize: 13, marginBottom: 12 }}>{openError}</p>
      )}

      {/* PIECE-0 — app-native role chrome, rendered AROUND the iframe (never
          inside it). roles.length === 0 (0/1-persona / non-switcher
          prototypes) means this is simply absent — no disabled placeholder
          needed, since there is nothing meaningful to switch between. */}
      {previewHtml !== null && roles.length > 0 && (
        <div
          data-testid="prototype-role-chrome"
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}
        >
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>View as:</span>
          {roles.map((role) => {
            const isActive = activeRoleIdx === null ? role.idx === 0 : activeRoleIdx === role.idx;
            return (
              <button
                key={role.idx}
                type="button"
                data-testid={`prototype-role-${role.idx}`}
                aria-pressed={isActive}
                disabled={opening}
                onClick={() => handleSelectRole(role.idx)}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6,
                  border: '1px solid var(--border)',
                  background: isActive ? 'var(--primary, #7c3aed)' : 'transparent',
                  color: isActive ? '#fff' : 'var(--text)',
                  cursor: opening ? 'default' : 'pointer',
                }}
              >
                {role.label}
              </button>
            );
          })}
        </div>
      )}

      {previewHtml !== null && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
            Sandboxed preview — scripted interactivity is disabled here for safety. Download the file to view it with full interactivity in your own browser.
          </p>
          {/* Security contract mirrors ScreenPreviewCard's own H1 finding:
              sandbox="" — EMPTY, never allow-scripts, never allow-same-origin.
              See this file's top-of-file comment for why. */}
          <iframe
            title="Prototype preview"
            data-testid="prototype-preview-frame"
            sandbox=""
            referrerPolicy="no-referrer"
            src={previewSrc}
            style={{ width: '100%', height: 600, border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }}
          />
        </div>
      )}

      {screenIds.length > 0 && (
        <div style={{ marginTop: 8, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <label htmlFor="prototype-screen-select" style={{ fontSize: 13, fontWeight: 600 }}>Annotate screen</label>
            <select
              id="prototype-screen-select"
              data-testid="prototype-screen-select"
              value={effectiveScreenId}
              onChange={(e) => setSelectedScreenId(e.target.value)}
            >
              {screenOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>

          {annotationsError && (
            <p role="alert" style={{ color: 'var(--error, #dc2626)', fontSize: 13, marginBottom: 8 }}>{annotationsError}</p>
          )}

          {/* Piece 3 — a small, fixed guided-review agenda for THIS screen,
              so a reviewer knows what to evaluate instead of facing a blank
              comment box. Static content, not per-project/LLM-generated. */}
          <div data-testid="prototype-guided-questions" style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
              Guided review questions
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {GUIDED_REVIEW_QUESTIONS.map((question) => {
                const answered = answeredGuidedLabels.has(question.label);
                const draft = guidedDrafts[question.id] ?? '';
                return (
                  <div
                    key={question.id}
                    data-testid={`prototype-guided-question-${question.id}`}
                    style={{ padding: 10, border: '1px solid var(--border)', borderRadius: 8 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{question.label}</span>
                      {answered && (
                        <span
                          data-testid={`prototype-guided-answered-${question.id}`}
                          style={{ fontSize: 11, fontWeight: 600, color: 'var(--success, #16a34a)' }}
                        >
                          Answered
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="text"
                        data-testid={`prototype-guided-answer-${question.id}`}
                        value={draft}
                        onChange={(e) => setGuidedDrafts((prev) => ({ ...prev, [question.id]: e.target.value }))}
                        placeholder="Answer this question…"
                        // R1/R2 nice-to-have: bound the ANSWER so the prefixed
                        // comment (buildGuidedAnnotationComment) can never
                        // exceed the backend's comment.max(2000) — unlike the
                        // free-comment textarea below, the prefix eats some of
                        // that budget, so the bound is per-question, not a
                        // flat 2000.
                        maxLength={2000 - buildGuidedAnnotationComment(question, '').length}
                        style={{ flex: 1, minWidth: 0 }}
                      />
                      <button
                        type="button"
                        data-testid={`prototype-guided-submit-${question.id}`}
                        disabled={guidedSavingId === question.id || !draft.trim()}
                        onClick={() => submitGuidedAnswer(question)}
                        style={{
                          fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6,
                          border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)',
                          cursor: guidedSavingId === question.id ? 'default' : 'pointer',
                        }}
                      >
                        {guidedSavingId === question.id ? 'Saving…' : 'Answer'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <textarea
            data-testid="prototype-annotation-comment"
            value={draftComment}
            onChange={(e) => setDraftComment(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Add a comment on this screen…"
            style={{ width: '100%', boxSizing: 'border-box', marginBottom: 8 }}
          />
          <button
            type="button"
            data-testid="prototype-annotation-save"
            disabled={saving || !draftComment.trim()}
            onClick={submitAnnotation}
            style={{ fontSize: 13, fontWeight: 600, padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer' }}
          >
            {saving ? 'Saving…' : 'Save comment'}
          </button>

          <AnnotationList annotations={annotations} />
        </div>
      )}
    </div>
  );
}
